import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, KeyRound, Trash2, Check, Copy, MailCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const inputCls = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm rounded-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none";

function fmtLastLogin(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "editor" });
  const [resetFor, setResetFor] = useState(null);
  const [resetLink, setResetLink] = useState(null);
  const [tempCreds, setTempCreds] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/admin/users"), api.get("/admin/roles")])
    .then(([u, r]) => { setUsers(u.data); setRoles(r.data); })
    .catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setOpen(false);
      setForm({ name: "", email: "", role: "editor" });
      if (data.email_sent) {
        toast.success(`Invitation email with a temporary password sent to ${data.email}`);
      } else {
        setTempCreds(data); // email failed — show temp password so admin can share it
      }
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const approve = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/approve`);
      setUsers(users.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
      toast.success(`${u.email} approved — notification email sent`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const patch = async (u, body) => {
    try {
      const { data } = await api.put(`/admin/users/${u.id}`, body);
      setUsers(users.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendResetLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${resetFor.id}/reset-password`);
      if (data.email_sent) toast.success(`Password reset link emailed to ${resetFor.email}`);
      else setResetLink({ email: resetFor.email, link: data.reset_link });
      setResetFor(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); setUsers(users.filter((x) => x.id !== u.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const th = "bg-zinc-50 border-b border-zinc-300 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium text-left";

  return (
    <div data-testid="admin-users-tab">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-user-button" className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium px-4 py-2 rounded-sm">
              <Plus size={15} /> Add user
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading tracking-tight">Add user</DialogTitle></DialogHeader>
            <form onSubmit={createUser} className="space-y-4 pt-2">
              <input data-testid="new-user-name" className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input data-testid="new-user-email" type="email" className={inputCls} placeholder="email@resolvetech.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <select data-testid="new-user-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
              <p className="text-xs text-zinc-500 flex items-start gap-1.5">
                <MailCheck size={14} className="mt-0.5 shrink-0 text-[#002FA7]" strokeWidth={1.5} />
                A temporary password will be generated and emailed to the user. They must set a new password on first sign-in.
              </p>
              <button data-testid="create-user-submit" disabled={busy} className="w-full bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium py-2.5 rounded-sm disabled:opacity-50">
                {busy ? "Creating…" : "Create user & send invite"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!users ? <p className="text-sm text-zinc-500">Loading…</p> : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className={th}>Name</th><th className={th}>Email</th><th className={th}>Role</th><th className={th}>Status</th><th className={th}>Created</th><th className={th}>Last login</th><th className={th}></th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-zinc-50">
                  <td className="border-b border-zinc-100 px-3 py-2 text-[13px]">{u.name || "—"}{u.id === me?.id && <span className="ml-2 text-[10px] text-[#002FA7] font-medium">YOU</span>}</td>
                  <td className="border-b border-zinc-100 px-3 py-2 text-[13px] font-mono">{u.email}</td>
                  <td className="border-b border-zinc-100 px-3 py-2">
                    <select data-testid={`user-role-select-${u.email}`} value={u.role} onChange={(e) => patch(u, { role: e.target.value })}
                      className="text-xs border border-zinc-200 rounded-sm px-2 py-1 bg-zinc-50 outline-none focus:border-[#002FA7]">
                      {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                  </td>
                  <td className="border-b border-zinc-100 px-3 py-2">
                    {u.pending ? (
                      <div className="flex items-center gap-2">
                        <span data-testid={`user-pending-badge-${u.email}`} className="text-[10px] font-medium uppercase tracking-wide px-2 py-1 rounded-sm bg-amber-50 text-amber-700">Pending approval</span>
                        <button data-testid={`user-approve-${u.email}`} onClick={() => approve(u)}
                          className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-2 py-1 rounded-sm bg-[#002FA7] text-white hover:bg-[#002280]">
                          <Check size={11} /> Approve
                        </button>
                      </div>
                    ) : (
                      <button data-testid={`user-active-toggle-${u.email}`} onClick={() => patch(u, { active: !u.active })}
                        className={`text-[10px] font-medium uppercase tracking-wide px-2 py-1 rounded-sm ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                        {u.active ? "Active" : "Deactivated"}
                      </button>
                    )}
                  </td>
                  <td className="border-b border-zinc-100 px-3 py-2 text-xs text-zinc-400 font-mono">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="border-b border-zinc-100 px-3 py-2 text-xs font-mono" data-testid={`user-last-login-${u.email}`}>
                    {u.last_login_at ? (
                      <span className="text-zinc-600">
                        {fmtLastLogin(u.last_login_at)}
                      </span>
                    ) : (
                      <span className="text-zinc-300 italic">Never</span>
                    )}
                  </td>
                  <td className="border-b border-zinc-100 px-3 py-2 text-right whitespace-nowrap">
                    <button data-testid={`user-reset-pwd-${u.email}`} title="Reset password" onClick={() => setResetFor(u)} className="text-zinc-400 hover:text-[#002FA7] p-1"><KeyRound size={14} strokeWidth={1.5} /></button>
                    <button data-testid={`user-delete-${u.email}`} title="Delete" onClick={() => remove(u)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 size={14} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!tempCreds} onOpenChange={(o) => !o && setTempCreds(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading tracking-tight">User created — email could not be sent</DialogTitle>
            <DialogDescription className="text-xs">The invitation email failed; share the temporary password manually.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-zinc-600">Share this temporary password with <b>{tempCreds?.email}</b> manually. They must change it on first sign-in.</p>
            <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2.5">
              <code data-testid="temp-password-value" className="text-sm flex-1">{tempCreds?.temp_password}</code>
              <button data-testid="copy-temp-password" onClick={() => { navigator.clipboard.writeText(tempCreds?.temp_password || ""); toast.success("Copied"); }}
                className="text-zinc-400 hover:text-[#002FA7] p-1"><Copy size={14} strokeWidth={1.5} /></button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Send password reset link</DialogTitle>
            <DialogDescription className="text-xs">A secure, single-use reset link (valid 1 hour) will be emailed to the user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-zinc-600">Email a password reset link to <b>{resetFor?.email}</b>?</p>
            <button data-testid="reset-pwd-submit" onClick={sendResetLink} disabled={busy}
              className="w-full bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium py-2.5 rounded-sm disabled:opacity-50">
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetLink} onOpenChange={(o) => !o && setResetLink(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Email could not be sent</DialogTitle>
            <DialogDescription className="text-xs">Share this reset link manually — it expires in 1 hour and works once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-zinc-600">Reset link for <b>{resetLink?.email}</b>:</p>
            <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2.5">
              <code data-testid="reset-link-value" className="text-xs flex-1 break-all">{resetLink?.link}</code>
              <button data-testid="copy-reset-link" onClick={() => { navigator.clipboard.writeText(resetLink?.link || ""); toast.success("Copied"); }}
                className="text-zinc-400 hover:text-[#002FA7] p-1 shrink-0"><Copy size={14} strokeWidth={1.5} /></button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
